import type { ReportTemplate } from '@safetyhub/shared';

/**
 * 문서관리 목록의 「유형」 필터 — **양식(템플릿) 축**의 순수 헬퍼.
 *
 * 모든 문서가 Composer 양식 문서이고 저장용 `type` 축은 없다. 사람이 「유형」이라 부르는 것은 양식 이름이고
 * 배지·제목이 이미 그렇게 보여 주므로(`getReportTypeLabel(templateName)`), 필터도 같은 축을 쓴다 —
 * 선택지는 **지금 이 사용자에게 켜진 Composer 양식**에서 파생하고, 질의는
 * `GET /reports?templateKey=`(`ReportsFilters.templateKey`)로 나간다.
 *
 * 선택지를 코드에 열거하지 않는 이유: 운영자가 저작하는 양식은 코드가 알 수 없다. 예전
 * `DOCUMENT_FILTER_TYPES`가 그래서 낡았다 — 배지에는 나오는데 필터에는 없는 유형(COMPOSED)이
 * 생겼다.
 */

/** URL 쿼리 키 — `?templateKey=<report_templates.key>`. */
export const TEMPLATE_KEY_PARAM = 'templateKey';

/**
 * 유형 축이던 시절의 쿼리 키. 진입 시 **읽지 않고**, URL 동기화가 지운다.
 *
 * `?type=TBM`·`?tab=tbm` 같은 옛 북마크는 구 유형 행이 없어 어느 쪽으로 읽어도 빈 목록이거나
 * 거짓 필터가 된다 — 읽지 않고 조용히 전체 목록으로 연다.
 */
export const LEGACY_TYPE_PARAMS = ['type', 'tab'] as const;

export interface TemplateFilterOption {
  /** `report_templates.key` — 질의 값 */
  value: string;
  /** 양식 이름 — 화면 라벨 */
  label: string;
}

/** 필터 선택지 파생에 필요한 최소 형상 — `ReportTemplate`이 이 subset을 만족한다. */
export type TemplateFilterSource = Pick<ReportTemplate, 'key' | 'name'>;

/**
 * 진입 URL이 지정한 양식 키. 없으면 빈 문자열(= 전체).
 *
 * 값을 검증하지 않는다 — 모르는 키는 서버가 빈 목록으로 답할 뿐 400이 아니고(상태 필터와
 * 다르다), 선택지 쪽이 그 값을 되살려 붙여 pill이 「전체」라 거짓말하지 않게 한다
 * (`resolveTemplateFilterOptions`).
 */
export function resolveEntryTemplateKey(params: URLSearchParams): string {
  return params.get(TEMPLATE_KEY_PARAM)?.trim() ?? '';
}

/**
 * 활성 양식 → 필터 선택지. 응답 순서를 그대로 둔다(양식 관리 화면과 같은 순서).
 *
 * 지금 고른 키가 선택지에 없으면(양식이 비활성화됐거나 다른 조직의 북마크) 그 키만 되살려
 * 붙인다 — 빠진 채로 두면 pill이 「전체」라 말하면서 목록은 그 양식만 보여 준다(표시가 거짓이
 * 된다). 이름을 모르므로 라벨은 키 그대로다. 다른 양식으로 옮기는 순간 사라지므로 **새로 고를
 * 수는 없다**.
 */
export function resolveTemplateFilterOptions(
  templates: readonly TemplateFilterSource[],
  selectedKey: string,
): TemplateFilterOption[] {
  const options = templates.map((template) => ({ value: template.key, label: template.name }));
  if (selectedKey && !options.some((option) => option.value === selectedKey)) {
    options.push({ value: selectedKey, label: selectedKey });
  }
  return options;
}
